/**
 * 「내 PC 에만 저장」 연결 — 서버 등록(이름·종류)과 이 브라우저 금고(값)를 묶는다 (서버 services/local_conns).
 *
 * 값이 서버로 가는 때: 연결 목록을 받을 때(조회·미리보기에 쓰려고), 「시험」, 「실행」 직전.
 * 서버는 메모리에만 두고 쓰지 않으면 잠시 뒤(기본 30분) 버린다. 실행은 실행 동안만 있는 임시 연결로 돈다.
 */
import { be1Fetch } from '../config/api';
import store from '../store';
import { errorFromResponse } from './errorText';
import { getPcConn, listPcConnIds, putPcConn, removePcConn, type PcConnValues } from './localVault';

export interface PcConnItem { connection_id: string; conn_type: string; at?: string; unlocked: boolean }

const currentUser = (): string => ((store.getState() as any).auth?.user?.loginId as string) || '';

async function json(res: Response) {
  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}

export async function listPcConns(): Promise<{ items: PcConnItem[]; types: string[]; ttlMin: number; here: string[] }> {
  const body = await json(await be1Fetch('/api/connections/local', { credentials: 'include' }));
  return { ...body, here: await listPcConnIds(currentUser()) };
}

/** 값을 서버 메모리로 보낸다 → 이 PC 에 값이 없으면 false */
export async function pushPcConn(id: string): Promise<boolean> {
  const v = await getPcConn(currentUser(), id);
  if (!v) return false;
  await json(await be1Fetch(`/api/connections/local/${encodeURIComponent(id)}/session`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ host: v.host, port: v.port, login: v.login, password: v.password, schema: v.schema, extra: v.extra }),
  }));
  return true;
}

/** 저장 — 이 PC 금고에 넣고, 서버에는 이름·종류만 등록한 뒤 값을 보낸다(곧 쓸 것이므로) */
export async function savePcConn(id: string, values: PcConnValues): Promise<void> {
  await json(await be1Fetch(`/api/connections/local/${encodeURIComponent(id)}`, {
    method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conn_type: values.conn_type }),
  }));
  await putPcConn(currentUser(), id, values);
  await pushPcConn(id);
}

export async function deletePcConn(id: string): Promise<void> {
  await json(await be1Fetch(`/api/connections/local/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' }));
  await removePcConn(currentUser(), id);
}

export async function testPcConn(id: string): Promise<void> {
  if (!(await pushPcConn(id))) throw new Error('missing');
  await json(await be1Fetch(`/api/connections/local/${encodeURIComponent(id)}/test`, { method: 'POST', credentials: 'include' }));
}

export const readPcConn = (id: string) => getPcConn(currentUser(), id);

/**
 * 이 연결들 가운데 「내 PC」 연결의 값을 서버로 보낸다 → 이 PC 에 값이 없는 연결 이름들(비어 있으면 모두 보냄).
 * 실행 직전에 부른다 — 서버가 다시 떴거나 시간이 지나 값을 버렸어도 실행이 막히지 않게.
 */
export async function pushPcConnsFor(ids: string[]): Promise<string[]> {
  const list = ((store.getState() as any).connections?.list || []) as Array<{ connection_id: string; storage?: string }>;
  const pc = new Set(list.filter((c) => c.storage === 'local').map((c) => c.connection_id));
  const missing: string[] = [];
  for (const id of Array.from(new Set(ids)).filter((x) => pc.has(x))) {
    try {
      if (!(await pushPcConn(id))) missing.push(id);
    } catch {
      missing.push(id);
    }
  }
  return missing;
}

/** 연결 목록을 받은 뒤 — 서버가 아직 값을 갖지 않은 「내 PC」 연결을 조용히 보낸다(조회·미리보기용) */
export function warmPcConns(list: Array<{ connection_id: string; storage?: string; unlocked?: boolean }>): void {
  const ids = list.filter((c) => c.storage === 'local' && !c.unlocked).map((c) => c.connection_id);
  ids.forEach((id) => { pushPcConn(id).catch(() => undefined); });
}

/** 캔버스 노드 설정의 연결 이름들 — 'connection' 키를 구조째 훑는다(서버 conn_scope._walk 와 같은 규칙) */
export function connIdsOf(nodes: Array<{ data?: any }>): string[] {
  const out = new Set<string>();
  const walk = (v: any) => {
    if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') {
      Object.entries(v).forEach(([k, x]) => {
        if (k === 'connection' && typeof x === 'string' && x.trim()) out.add(x.trim());
        else walk(x);
      });
    }
  };
  (nodes || []).forEach((n) => walk(n?.data?.config));
  return Array.from(out);
}

